import { BuilderActionInput } from "@src/types.generated";

const COURSE_ACTION_LABELS: Record<string, string> = {
  addLessonAction: "Added a section",
  addMcOptionAction: "Added an answer option",
  addPromptAction: "Added a screen",
  addResponseOptionAction: "Added an answer option",
  moveLessonAction: "Reordered a section",
  movePromptAction: "Reordered a screen",
  moveResponseOptionAction: "Reordered an answer option",
  removeLessonAction: "Removed a section",
  removeMcOptionAction: "Removed an answer option",
  removePromptAction: "Removed a screen",
  removeResponseOptionAction: "Removed an answer option",
  setCourseCoverImageAction: "Updated the cover image",
  setCourseDescriptionAction: "Updated the course description",
  setCourseNameAction: "Renamed the course",
  setCourseSettingsAction: "Updated course settings",
  setGradedFreeResponseCorrectAnswerAction: "Updated a correct answer",
  setLessonNameAction: "Renamed a section",
  setMcCorrectOptionAction: "Updated a correct answer",
  setMcOptionAction: "Edited an answer option",
  setPromptAssignMediaAction: "Updated screen media",
  setPromptImageOnlyMatchItemsAction: "Updated a matching question",
  setPromptMediaAction: "Updated screen media",
  setPromptRequireFullVideoPlaybackAction: "Updated a video screen",
  setPromptSelectAllThatApplyAction: "Updated a question",
  setPromptRandomizeOptionOrderAction: "Updated answer randomization",
  setPromptTextAction: "Edited a screen",
  setPromptTypeAction: "Changed a screen type",
  setResponseOptionAnswerAction: "Updated an answer",
  setResponseOptionIsCorrectAction: "Updated a correct answer",
  setResponseOptionMatchItemAction: "Updated a matching answer",
  setResponseOptionMediaAction: "Updated answer media",
  setTrueFalseCorrectOptionAction: "Updated a correct answer",
};

const MODULE_ACTION_LABELS: Record<string, string> = {
  addModuleContentMembershipAction: "Added content to the module",
  moveModuleContentMembershipAction: "Reordered module content",
  removeModuleContentMembershipAction: "Removed content from the module",
  setCourseOutlineBodyTextAction: "Edited the outline",
  setCourseOutlineTitleTextAction: "Edited an outline heading",
  setModuleContentMembershipRequireFullVideoPlaybackAction:
    "Updated module content settings",
  setModuleCoverImageAction: "Updated the cover image",
  setModuleDescriptionAction: "Updated the module description",
  setModuleNameAction: "Renamed the module",
  setModuleSettingsAction: "Updated module settings",
  setModuleVersionSettingsAction: "Updated module settings",
  setSectionBreakLockBetweenSectionDaysAction: "Updated a section break",
  setSectionBreakTitleAction: "Renamed a section",
  startCourseOutlineGenerationAction: "Generating a course outline",
};

const SKILL_ACTION_LABELS: Record<string, string> = {
  addConditionalStepGroupAction: "Added a conditional group",
  addStepAction: "Added a step",
  addStepResponseOptionAction: "Added an answer option",
  moveStepAction: "Reordered a step",
  moveStepResponseOptionAction: "Reordered an answer option",
  removeConditionalStepGroupAction: "Removed a conditional group",
  removeStepAction: "Removed a step",
  removeStepResponseOptionAction: "Removed an answer option",
  setSkillCoverImageAction: "Updated the cover image",
  setSkillDescriptionAction: "Updated the check-in description",
  setSkillNameAction: "Renamed the check-in",
  setSkillSettingsAction: "Updated check-in settings",
  setSkillVersionHeaderLibraryItemLinkAction: "Linked reference content",
  setSkillVersionHeaderMediaAction: "Updated header media",
  setSkillVersionPassingThresholdPercentageAction: "Updated the passing score",
  setStepDescriptionAction: "Edited a step",
  setStepFailedSuggestionTextAction: "Edited step feedback",
  setStepIsRequiredAction: "Updated a step",
  setStepLibraryItemLinkAction: "Linked reference content",
  setStepMediaAction: "Updated step media",
  setStepNoteRequiredAction: "Updated a step",
  setStepPhotoRequiredAction: "Updated a step",
  setStepResponseOptionIsCorrectAction: "Updated a correct answer",
  setStepResponseOptionPointsAction: "Updated answer points",
  setStepResponseOptionTextAction: "Edited an answer option",
  setStepTemperatureUnitAction: "Updated a step",
  setStepTextAction: "Edited a step",
  setStepTypeAction: "Changed a step type",
  setStepVideoRequiredAction: "Updated a step",
  setVerificationStepTriggerAction: "Updated a trigger",
};

const TRAINING_RESOURCE_ACTION_LABELS: Record<string, string> = {
  setRichTextDocumentContentAction: "Edited the document",
  setTrainingResourceNameAction: "Renamed the resource",
  setTrainingResourceVersionTypeAction: "Changed the resource type",
};

const ID_FIELDS = new Set([
  "courseId",
  "moduleId",
  "skillId",
  "trainingResourceId",
]);

const GENERIC_FALLBACK = "Making changes";

const firstActionKey = (
  obj: Record<string, unknown> | null | undefined,
): string | undefined => {
  if (!obj) return undefined;
  return Object.keys(obj).find(
    (key) => !ID_FIELDS.has(key) && obj[key] != null,
  );
};

const describeFrom = (
  obj: Record<string, unknown> | null | undefined,
  labels: Record<string, string>,
): string | undefined => {
  const key = firstActionKey(obj);
  if (!key) return undefined;
  return labels[key] ?? GENERIC_FALLBACK;
};

export const describeBuilderAction = (
  payload: BuilderActionInput | null | undefined,
): string => {
  if (!payload) return GENERIC_FALLBACK;

  return (
    describeFrom(payload.courseBuilderAction, COURSE_ACTION_LABELS) ??
    describeFrom(payload.moduleBuilderAction, MODULE_ACTION_LABELS) ??
    describeFrom(payload.skillBuilderAction, SKILL_ACTION_LABELS) ??
    describeFrom(
      payload.trainingResourceBuilderAction,
      TRAINING_RESOURCE_ACTION_LABELS,
    ) ??
    GENERIC_FALLBACK
  );
};
